/**
 * Remote Config Service
 * Fetches and provides access to Firebase Remote Config values
 */
import { Injectable, inject, Injector, runInInjectionContext, Optional } from '@angular/core';
import { RemoteConfig, fetchAndActivate, getValue } from '@angular/fire/remote-config';
import { BehaviorSubject, Observable } from 'rxjs';
import { RemoteConfigDefaults, EffectiveConfig, TenantConfig } from '../models/config.types';

type RemoteConfigWithSettings = RemoteConfig & {
  settings?: {
    minimumFetchIntervalMillis?: number;
    fetchTimeoutMillis?: number;
  };
};

/**
 * Hard upper bound (ms) for the entire Remote Config bootstrap.
 * Even if the Firebase SDK ignores `fetchTimeoutMillis` (e.g. when stuck in
 * exponential-backoff throttle stored in IndexedDB), this Promise.race ceiling
 * guarantees the app never blocks on Remote Config during startup.
 */
const REMOTE_CONFIG_BOOTSTRAP_HARD_TIMEOUT_MS = 7000;
const REMOTE_CONFIG_FETCH_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private remoteConfig: RemoteConfig | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private injector = inject(Injector);
  private configDefaults: RemoteConfigDefaults = {
    pagination_default_limit: 10,
    pagination_max_limit: 10,
    api_base_url: '',
    form_listener_timeout_hours: 1,
    feature_flags: {},
  };

  private effectiveConfigSubject = new BehaviorSubject<EffectiveConfig | null>(null);
  public effectiveConfig$: Observable<EffectiveConfig | null> =
    this.effectiveConfigSubject.asObservable();

  /**
   * Initialize Remote Config
   */
  async initialize(): Promise<void> {
    try {
      this.updateEffectiveConfig(this.configDefaults, null);
    } catch (error) {
      console.error('Failed to initialize Remote Config:', error);
      this.updateEffectiveConfig(this.configDefaults, null);
    }
  }

  /**
   * Initialize with Remote Config instance.
   * Bounded by a hard timeout so a hung or throttled Remote Config fetch can
   * never block app bootstrap. On any failure (timeout, network, throttle) the
   * service falls back to local `configDefaults` and keeps polling in the
   * background, so the app stays responsive.
   */
  async initializeWithRemoteConfig(remoteConfig: RemoteConfig): Promise<void> {
    this.remoteConfig = remoteConfig;

    try {
      await runInInjectionContext(this.injector, async () => {
        const rc: RemoteConfigWithSettings = remoteConfig;
        if (rc.settings) {
          rc.settings.minimumFetchIntervalMillis = 0; // Allow immediate fetches
          rc.settings.fetchTimeoutMillis = REMOTE_CONFIG_FETCH_TIMEOUT_MS;
        }

        const fetchPromise = fetchAndActivate(remoteConfig);
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () =>
              reject(
                new Error(
                  `Remote Config bootstrap exceeded ${REMOTE_CONFIG_BOOTSTRAP_HARD_TIMEOUT_MS}ms; falling back to defaults`,
                ),
              ),
            REMOTE_CONFIG_BOOTSTRAP_HARD_TIMEOUT_MS,
          ),
        );

        await Promise.race([fetchPromise, timeoutPromise]);
      });

      this.updateConfigFromRemoteConfig(remoteConfig);
      this.startPollingUpdates(remoteConfig);
    } catch (error) {
      console.warn('Remote Config unavailable at startup; using defaults:', error);
      this.updateEffectiveConfig(this.configDefaults, null);
      this.startPollingUpdates(remoteConfig);
    }
  }

  /**
   * Update config from Remote Config values
   */
  private updateConfigFromRemoteConfig(remoteConfig: RemoteConfig): void {
    try {
      runInInjectionContext(this.injector, () => {
        const paginationDefaultLimit = getValue(remoteConfig, 'pagination_default_limit');
        const paginationMaxLimit = getValue(remoteConfig, 'pagination_max_limit');
        const apiBaseUrl = getValue(remoteConfig, 'api_base_url');
        const formListenerTimeoutHours = getValue(remoteConfig, 'form_listener_timeout_hours');

        const defaults: RemoteConfigDefaults = {
          pagination_default_limit: paginationDefaultLimit.asNumber() || 10,
          pagination_max_limit: paginationMaxLimit.asNumber() || 10,
          api_base_url: apiBaseUrl.asString() || '',
          form_listener_timeout_hours: formListenerTimeoutHours.asNumber() || 1,
          feature_flags: {},
        };

        this.configDefaults = defaults;
        this.updateEffectiveConfig(defaults, null);

        // Log config to console
        console.info('[Remote Config] Loaded configuration:', defaults);
        const effective = this.getEffectiveConfig();
        if (effective) {
          console.info('[Remote Config] Effective configuration:', effective);
        }
      });
    } catch (error) {
      console.error('Failed to read Remote Config values:', error);
    }
  }

  /**
   * Start polling for Remote Config updates
   * AngularFire doesn't support real-time updates, so we poll periodically
   */
  private startPollingUpdates(remoteConfig: RemoteConfig): void {
    const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

    this.pollingInterval = setInterval(async () => {
      try {
        const activated = await runInInjectionContext(this.injector, async () => {
          return await fetchAndActivate(remoteConfig);
        });

        if (activated) {
          this.updateConfigFromRemoteConfig(remoteConfig);
        }
      } catch (error) {
        console.error('Error polling for Remote Config updates:', error);
      }
    }, POLL_INTERVAL_MS);
  }

  /**
   * Stop polling for updates
   */
  stopRealTimeListener(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Manually fetch and activate Remote Config
   * Returns true if new config was activated, false if no changes
   */
  async fetchAndActivateRemoteConfig(): Promise<boolean> {
    if (!this.remoteConfig) {
      throw new Error('Remote Config not initialized');
    }

    try {
      const activated = await runInInjectionContext(this.injector, async () => {
        return await fetchAndActivate(this.remoteConfig!);
      });

      if (activated) {
        this.updateConfigFromRemoteConfig(this.remoteConfig);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error fetching Remote Config:', error);
      throw error;
    }
  }

  /**
   * Get pagination limit (with tenant override)
   */
  getPaginationLimit(tenantOverride?: number | null): number {
    if (tenantOverride !== undefined && tenantOverride !== null) {
      return tenantOverride;
    }
    return this.configDefaults.pagination_default_limit;
  }

  /**
   * Get max pagination limit (with tenant override)
   */
  getMaxPaginationLimit(tenantOverride?: number | null): number {
    if (tenantOverride !== undefined && tenantOverride !== null) {
      return tenantOverride;
    }
    return this.configDefaults.pagination_max_limit;
  }

  /**
   * Get feature flag value
   */
  getFeatureFlag(name: string, tenantOverride?: boolean | null): boolean {
    if (tenantOverride !== undefined && tenantOverride !== null) {
      return tenantOverride;
    }
    return this.configDefaults.feature_flags[name] || false;
  }

  /**
   * Get API base URL
   */
  getApiUrl(): string {
    return this.configDefaults.api_base_url;
  }

  /**
   * Get effective config
   */
  getEffectiveConfig(): EffectiveConfig | null {
    return this.effectiveConfigSubject.value;
  }

  /**
   * Update effective config (called by TenantConfigService)
   */
  updateEffectiveConfig(defaults: RemoteConfigDefaults, tenantConfig: TenantConfig | null): void {
    const effective: EffectiveConfig = {
      paginationLimit: tenantConfig?.paginationLimit ?? defaults.pagination_default_limit,
      maxPaginationLimit: tenantConfig?.maxPaginationLimit ?? defaults.pagination_max_limit,
      featureFlags: {
        ...defaults.feature_flags,
        ...(tenantConfig?.featureFlags || {}),
      },
      apiBaseUrl: defaults.api_base_url,
      formListenerTimeoutHours: defaults.form_listener_timeout_hours,
    };

    this.effectiveConfigSubject.next(effective);
  }

  /**
   * Get form listener timeout in milliseconds
   */
  getFormListenerTimeoutMs(): number {
    const config = this.getEffectiveConfig();
    const hours = config?.formListenerTimeoutHours ?? 1;
    return hours * 60 * 60 * 1000; // Convert hours to milliseconds
  }
}
